/**
 * gameCreator/brief.js - the two blocks of text this page exists to produce.
 *
 * ONE: the brief a person pastes into a fresh Claude Code session.
 * TWO: the terminal commands that put the finished game into production.
 *
 * WHY THE BRIEF IS SHORT
 * A session opened in this repository reads CLAUDE.md on its own. Restating
 * the framework, the contract or the rules in section 5 here would give the
 * session two sources for the same thing, and the moment CLAUDE.md changed the
 * brief would be quietly wrong. So the brief carries only what CLAUDE.md
 * cannot know: which game, which coverage, and the standing instructions about
 * this machine - no build, no psql, no hand-written rows.
 *
 * WHY THE DEPLOY COMMANDS ARE SEPARATE STEPS
 * They are run one at a time by a person, and each one depends on the last
 * having succeeded. A single copyable blob invites pasting the lot, which
 * would apply a migration to the live database before the clone had proved it
 * and before the backup existed. CLAUDE.md section 4 is explicit that this is
 * the failure worth engineering against.
 *
 * Pure: takes a plain object, returns strings. Tested in
 * client/test/adminGameCreator.test.js.
 */

import { summariseSelection, requiredCapabilities, selectionTotals } from './model.js';

/* =========================================================================
 * THIS MACHINE
 *
 * The one thing on this page that is not derived from code. Paths and names
 * come from README.md (deployment) and server/.env (DATABASE_URL). They are
 * gathered here so there is one place to correct them, and the page says out
 * loud that they should be checked rather than trusted.
 * ========================================================================= */

export const DEPLOY_ENV = Object.freeze({
  repo: '/var/www/speechformation',
  database: 'speechformation',
  clone: 'speechformation_clone',
  process: 'speechformation',
});

/** Kebab-case game id from a typed name. The id is a database key: keep it plain. */
export function slugify(name) {
  return String(name || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
}

/**
 * The game id every generated path uses.
 * When the person has asked Claude to choose the name there is no id yet, so
 * the placeholder goes through verbatim and the brief tells Claude to replace
 * it. A guessed id would end up in a SQL file and in games.id, which is a
 * foreign key from every attempt and every box score.
 */
export function gameSlug(spec) {
  if (spec.letClaudeName) return '<game-id>';
  return slugify(spec.name) || '<game-id>';
}

function gameTitle(spec) {
  if (spec.letClaudeName) return '<game name>';
  return (spec.name || '').trim() || '<game name>';
}

/** Two-digit sequence for the game doc, from how many games are registered. */
function gameNumber(spec) {
  const n = Number.isInteger(spec.existingGameCount) ? spec.existingGameCount + 1 : null;
  return n === null ? '<nn>' : String(n).padStart(2, '0');
}

function block(heading, body) {
  if (!body || !String(body).trim()) return '';
  return `\n${heading}\n${String(body).trim()}\n`;
}

/* =========================================================================
 * THE BRIEF
 * ========================================================================= */

/**
 * @param {{name:string, letClaudeName:boolean, description:string,
 *          setting:string, vocabulary:string,
 *          selected:Object<string,boolean>, sketches:Object<string,string>,
 *          existingGameCount:number}} spec
 * @returns {string}
 */
export function buildBrief(spec) {
  const slug = gameSlug(spec);
  const title = gameTitle(spec);
  const phases = summariseSelection(spec.selected);
  const totals = selectionTotals(spec.selected);
  const caps = requiredCapabilities(spec.selected);
  const sketches = spec.sketches || {};

  const out = [];

  out.push('Build a new game for this platform.');
  out.push('');
  out.push('Read CLAUDE.md first and follow it. It holds the framework, the nine-point');
  out.push('contract and the rules in section 5. This brief carries only what CLAUDE.md');
  out.push('cannot know: which game, what it covers, and what to do when it works.');

  out.push('');
  out.push('THE GAME');
  if (spec.letClaudeName) {
    out.push('Name    Choose one. Short, concrete, and sayable by a four-year-old.');
    out.push('Id      Kebab-case of the name. Use it everywhere <game-id> appears below.');
  } else {
    out.push(`Name    ${title}`);
    out.push(`Id      ${slug}`);
  }
  out.push(`Folder  client/src/components/game/${slug}/`);

  const description = (spec.description || '').trim();
  out.push('');
  out.push(description || '<< describe the idea here - this brief is thin without it >>');

  out.push(block('SETTING AND CHARACTERS', spec.setting).trimEnd());
  out.push(block('VOCABULARY', spec.vocabulary).trimEnd());

  /* ---- coverage ---- */
  out.push('');
  if (!phases.length) {
    out.push('COVERAGE');
    out.push('<< none ticked - go back and choose the phases and levels this game covers >>');
  } else {
    out.push(
      `COVERAGE  -  ${totals.levels} ${totals.levels === 1 ? 'level' : 'levels'} across `
      + `${phases.length} ${phases.length === 1 ? 'phase' : 'phases'}, ${totals.boxes} boxes`
    );
    out.push('Ask engine/contract.js describeLevelContract() which boxes each level owes.');
    out.push('The count is not constant even inside one phase.');

    for (const phase of phases) {
      out.push('');
      out.push(`Phase ${phase.phase}  ${phase.name}  -  ${phase.plain}`);
      out.push(`  prompts: ${phase.promptStyle} (${phase.promptMustInclude}).  accepts: ${phase.strictnessAccepts}`);
      for (const level of phase.levels) {
        out.push(`  Level ${level.level}  ${level.intent}`);
        const sketch = (sketches[level.key] || '').trim();
        if (sketch) {
          for (const line of sketch.split('\n')) out.push(`           ${line.trim()}`);
        }
      }
    }
  }

  /* ---- capabilities ---- */
  if (caps.length) {
    out.push('');
    out.push('CAPABILITIES THIS COVERAGE NEEDS');
    for (const cap of caps) {
      out.push(
        `  ${cap.id}  -  ${cap.boxes} of the ${totals.boxes} boxes above depend on it `
        + `(phase${cap.phases.length > 1 ? 's' : ''} ${cap.phases.join(', ')}).`
      );
    }
    out.push('  Declare them in content/index.js AND in the SQL, and actually record the');
    out.push('  signal. A capability declared and not delivered is worse than none: the');
    out.push('  boxes are scored from evidence that was never gathered.');
  }

  if (totals.observational) {
    out.push('');
    out.push(
      `${totals.observational} of the boxes above ${totals.observational === 1 ? 'is' : 'are'} `
      + 'observational. No game scores them, in any configuration. The engine excludes'
    );
    out.push('them from what you owe. Do not invent a proxy for one.');
  }

  /* ---- standing instructions ---- */
  out.push('');
  out.push('WHAT TO DO');
  out.push('1. Read client/src/components/game/door-knock/ before writing anything. It is');
  out.push('   the smaller of the two existing games and the shape a new one takes.');
  out.push('2. Do not run a build. `cd client && npm test` is the only command to run.');
  out.push('3. Do not run psql, any migration, or your own registration file, and do not');
  out.push('   write, edit or delete a row by hand. The database is live and has a real');
  out.push(`   child's record in it. Write database/register_${slug}.sql - additive and`);
  out.push('   idempotent, copy register_door_knock.sql - and hand it over unapplied.');
  out.push('4. Register the game in all four places:');
  out.push(`     database/register_${slug}.sql`);
  out.push('     client/src/pages/GamePlayer.jsx');
  out.push('     client/test/support/manifests.js');
  out.push('     client/test/support/adapters.js  (so the simulation can play it)');
  out.push('5. Run `cd client && npm test` until everything is green - the conformance');
  out.push('   suite against your game, and a simulated session that gets a child');
  out.push('   through your content without sticking.');
  out.push(`6. Write docs/audit/GAME-${gameNumber(spec)}-${slug}.md: what the game is, every`);
  out.push('   judgement call marked ENGINE DECISION with its reasoning, and - the part');
  out.push('   that matters most - everything CLAUDE.md did not tell you that you turned');
  out.push('   out to need. That section is how the next author gets told.');

  return out.join('\n').replace(/\n{3,}/g, '\n\n').trim() + '\n';
}

/* =========================================================================
 * THE DEPLOY COMMANDS
 * ========================================================================= */

/**
 * @param {object} spec the same object buildBrief() takes
 * @returns {Array<{id:string, title:string, note:string, command:string}>}
 *          in the order they are run. Each depends on the one before it.
 */
export function buildDeploySteps(spec) {
  const slug = gameSlug(spec);
  const title = gameTitle(spec);
  const totals = selectionTotals(spec.selected);
  const phases = summariseSelection(spec.selected);
  const phaseList = phases.map((p) => p.phase).join(', ') || '<phases>';
  const { repo, database, clone, process: proc } = DEPLOY_ENV;
  const sql = `${repo}/database/register_${slug}.sql`;

  return [
    {
      id: 'status',
      title: 'See what changed',
      note: 'Everything the session touched, and nothing you did not expect.',
      command: `cd ${repo} && git status && git diff --stat`,
    },
    {
      id: 'test',
      title: 'Run the suite yourself',
      note: 'The conformance checks and a simulated session, against the real engine '
        + 'and the real API over an in-memory database. Green here proves the engine '
        + 'is right, not that the schema is.',
      command: `cd ${repo}/client && npm test`,
    },
    {
      id: 'commit',
      title: 'Commit',
      note: 'Before anything touches a database, so there is something to go back to.',
      command: [
        `cd ${repo}`,
        'git add -A',
        `git commit -m "Add ${title}: ${totals.levels} levels across phases ${phaseList}"`,
      ].join('\n'),
    },
    {
      id: 'clone',
      title: 'Apply the migration to a clone',
      note: `A migration is only really tested by applying it as the role that will `
        + `apply it in production. createdb -T needs no other session connected to `
        + `${database}, so stop the app first if it holds one.`,
      command: [
        `sudo -u postgres createdb -T ${database} ${clone}`,
        `psql -d ${clone} -v ON_ERROR_STOP=1 -f ${sql}`,
      ].join('\n'),
    },
    {
      id: 'idempotent',
      title: 'Prove it is idempotent',
      note: 'Every file in database/ is additive and idempotent. The second run must '
        + 'succeed with no error and leave exactly one row, whose coverage and '
        + 'capabilities match what the game declares in content/.',
      command: [
        `psql -d ${clone} -v ON_ERROR_STOP=1 -f ${sql}`,
        `psql -d ${clone} -c "SELECT id, total_levels, engine_capabilities, engine_coverage `
          + `FROM games WHERE id = '${slug}';"`,
      ].join('\n'),
    },
    {
      id: 'backup',
      title: 'Back up production',
      note: 'There is a real child record in there with real scored attempts against '
        + 'it. Take the backup even though this migration only inserts a row.',
      command: [
        'mkdir -p ~/backups',
        `pg_dump "$DATABASE_URL" -Fc -f ~/backups/${database}-$(date +%Y%m%d-%H%M%S).dump`,
      ].join('\n'),
    },
    {
      id: 'apply',
      title: 'Apply to production',
      note: 'The server reads games.engine_coverage, not the client. Until this runs, '
        + 'the game covers nothing and every attempt it posts is refused.',
      command: `psql "$DATABASE_URL" -v ON_ERROR_STOP=1 -f ${sql}`,
    },
    {
      id: 'dropclone',
      title: 'Drop the clone',
      note: 'It is a full copy of a child\'s record. Do not leave it lying about.',
      command: `sudo -u postgres dropdb ${clone}`,
    },
    {
      id: 'build',
      title: 'Build the client',
      note: 'The only build in the whole process, and it happens here rather than in '
        + 'the Claude session.',
      command: `cd ${repo}/client && npm run build`,
    },
    {
      id: 'restart',
      title: 'Restart and watch',
      note: 'Watch the log while someone plays one level. A coverage mismatch shows up '
        + 'as a refused attempt, and the refusal is silent to the child.',
      command: `pm2 restart ${proc} && pm2 logs ${proc} --lines 40`,
    },
  ];
}

/** The deploy panel as one block, for the person who wants the lot in a file. */
export function buildDeployScript(spec) {
  return buildDeploySteps(spec)
    .map((step, i) => `# ${i + 1}. ${step.title}\n# ${step.note}\n${step.command}`)
    .join('\n\n') + '\n';
}

export default { DEPLOY_ENV, slugify, gameSlug, buildBrief, buildDeploySteps, buildDeployScript };
